import { useQuery } from "@apollo/client";
import { Box, Typography, MenuItem, Alert } from "@mui/material";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { ReactElement } from "react";
import { Form, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { createOrEditSubaccountInputSchema } from "@stw/yup-validators";

import {
  EditSubaccountBasicsMutation,
  EditSubaccountBasicsMutationVariables,
  GetSubaccountBudgetDataQuery,
  GetSubaccountBudgetDataQueryVariables,
  UserTenantRole,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink, InternalLink } from "~frontend/components/Link";
import { PageHeader } from "~frontend/components/PageHeader";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { FormikDurationField } from "~frontend/components/forms/FormikDurationField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import FormikSwitchField from "~frontend/components/forms/FormikSwitchField";
import FormikTextingHoursFields from "~frontend/components/forms/FormikTextingHoursFields";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { LOGGED_IN_INFO } from "~frontend/graphql/loggedInInfo";
import {
  EDIT_SUBACCOUNT_BASICS,
  GET_SUBACCOUNT_BUDGET_DATA,
} from "~frontend/graphql/subaccounts";
import {
  useCurrentUser,
  useTenant,
} from "~frontend/providers/LoggedInUserProvider";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { BudgetDetails } from "~frontend/routes/organization/budgets/BudgetDetails";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import SettingsPreviewContacts from "./SettingsPreviewContacts";

const DEFAULT_OPT_OUT_MESSAGE =
  "You have been opted-out of messages from this sender.";

export default function SettingsForm(): ReactElement {
  const navigate = useNavigate();
  const getErrorMessage = useErrorMessageFormatter();
  const tenant = useTenant()!;
  const snackbar = useSnackbar();
  const subaccount = useSubaccountAdminConfig()!;
  const client = useNonCacheClearingApolloClient();
  const currentUser = useCurrentUser()!;

  const {
    data: subaccountBudgetData,
    loading: subaccountBudgetLoading,
    error: subaccountBudgetError,
  } = useQuery<
    GetSubaccountBudgetDataQuery,
    GetSubaccountBudgetDataQueryVariables
  >(GET_SUBACCOUNT_BUDGET_DATA, {
    variables: { input: { subaccountId: subaccount.id } },
    fetchPolicy: "cache-and-network",
  });

  const [editSubaccountBasics] = useMutation<
    EditSubaccountBasicsMutation,
    EditSubaccountBasicsMutationVariables
  >(EDIT_SUBACCOUNT_BASICS, {
    onErrorSnackbarMessage: "Failed to update subaccount",
    refetchQueries: [LOGGED_IN_INFO],
    awaitRefetchQueries: true,
    client,
  });

  const userIsOrgOwnerOrHigher = currentUser.tenantRole !== UserTenantRole.None;

  return (
    <Formik
      initialValues={{
        [submitErrorField]: null,
        name: subaccount.name,
        slug: subaccount.slug,
        textingHoursTimezone: subaccount.textingHoursTimezone,
        textingHoursStart: subaccount.textingHoursStart,
        textingHoursEnd: subaccount.textingHoursEnd,
        linkShortenerEnabled: !!subaccount.defaultLinkShortenerDomainName,
        linkShortenerDomainName:
          subaccount.defaultLinkShortenerDomainName ??
          subaccount.linkShortenerDomains[0]?.domain ??
          "",
        autoAddRefcodesToABLinks: subaccount.autoAddRefcodesToABLinks,
        texterSendEnabled: subaccount.texterSendEnabled,
        texterAssignmentBatchSize: subaccount.texterAssignmentBatchSize,
        texterAssignmentExpirationMinutes:
          subaccount.texterAssignmentExpirationMinutes,
        optOutMessageEnabled: Boolean(subaccount.optOutMessage),
        optOutMessage: subaccount.optOutMessage ?? "",
      }}
      validationSchema={
        createOrEditSubaccountInputSchema(Yup, true).fields.input
      }
      onSubmit={async (values, formik) => {
        const { errors, data } = await editSubaccountBasics({
          variables: {
            input: {
              subaccountId: subaccount.id,
              name: values.name,
              slug: values.slug,
              textingHoursTimezone: values.textingHoursTimezone,
              textingHoursStart: values.textingHoursStart,
              textingHoursEnd: values.textingHoursEnd,
              defaultLinkShortenerDomainName: values.linkShortenerEnabled
                ? values.linkShortenerDomainName || null
                : null,
              autoAddRefcodesToABLinks: values.autoAddRefcodesToABLinks,
              texterAssignmentBatchSize: values.texterAssignmentBatchSize,
              texterAssignmentExpirationMinutes:
                values.texterAssignmentExpirationMinutes,
              optOutMessage: values.optOutMessageEnabled
                ? values.optOutMessage
                : null,
              texterSendEnabled: values.texterSendEnabled,
            },
          },
        });

        if (errors) {
          formik.setErrors({
            [submitErrorField]: getErrorMessage(errors),
          });
        } else if (data) {
          const newData = data!.editSubaccountBasics;

          if (newData.slug !== subaccount.slug) {
            navigate(`/subaccounts/${newData.slug}/settings`)?.catch(
              console.error
            );
          }

          formik.resetForm({
            values: {
              [submitErrorField]: null,
              name: newData.name,
              slug: newData.slug,
              textingHoursTimezone: newData.textingHoursTimezone,
              textingHoursStart: newData.textingHoursStart,
              textingHoursEnd: newData.textingHoursEnd,
              linkShortenerEnabled: !!newData.defaultLinkShortenerDomainName,
              linkShortenerDomainName:
                newData.defaultLinkShortenerDomainName ??
                subaccount.linkShortenerDomains[0]?.domain ??
                "",
              autoAddRefcodesToABLinks: newData.autoAddRefcodesToABLinks,
              texterAssignmentBatchSize: newData.texterAssignmentBatchSize,
              texterAssignmentExpirationMinutes:
                newData.texterAssignmentExpirationMinutes,
              optOutMessage: newData.optOutMessage ?? "",
              optOutMessageEnabled: !!newData.optOutMessage,
              texterSendEnabled: newData.texterSendEnabled,
            },
          });

          snackbar.enqueueSnackbar("Subaccount settings saved", {
            variant: "success",
          });
        }
      }}
    >
      {({ values, submitForm, setFieldValue }) => (
        <Form>
          <FormikErrorBanner />

          <FlexBox direction="column" gap={3}>
            <Box>
              <PageHeader isSlim>Basics</PageHeader>
              <Typography variant="h2" sx={{ mb: 2 }}>
                Choose your Subaccount Name and the time range when your
                campaigns can send texts.
              </Typography>

              <Box sx={{ mb: 1 }}>
                <FormikTextField
                  name="name"
                  label={
                    tenant.subaccountsEnabled
                      ? "Subaccount Name"
                      : "Organization Name"
                  }
                  fullWidth
                  inputProps={{ "data-testid": "edit-subaccount-name-field" }}
                />
              </Box>

              {tenant.subaccountsEnabled && (
                <Box sx={{ mb: 1 }}>
                  <FormikTextField
                    name="slug"
                    label="Subaccount Slug"
                    fullWidth
                  />
                  {values.slug !== subaccount.slug && (
                    <Alert severity="warning">
                      Editing this will break all existing links, including
                      invite links and thread links, for the subaccount.
                    </Alert>
                  )}
                </Box>
              )}

              <FormikTextingHoursFields label="Texting Hours" />

              <FlexBox gap={2} sx={{ width: "100%" }}>
                <FormikTextField
                  name="texterAssignmentBatchSize"
                  label="Texter Assignment Batch Size"
                  type="number"
                  bottomLabel="The number of replies that will be assigned to a texter at a time"
                  sx={{ flex: 1 }}
                />
                <FormikDurationField
                  name="texterAssignmentExpirationMinutes"
                  label="Assignment Duration"
                  bottomLabel="After this amount of time, assignments will go back into the pool for reassignment"
                />
              </FlexBox>

              <FormikSwitchField
                name="optOutMessageEnabled"
                label="Send Automatic Opt Out Message"
                switchProps={{
                  onChange: (_e, checked) =>
                    setFieldValue(
                      "optOutMessage",
                      checked ? DEFAULT_OPT_OUT_MESSAGE : ""
                    ),
                }}
              />

              <Typography sx={{ pt: 1, pb: 2 }} variant="body1">
                This message will be automatically sent to contacts who reply to
                a message with an opt out phrase. This message will be sent
                whenever the contact replies, even if it is outside of texting
                hours.
              </Typography>

              {values.optOutMessageEnabled && (
                <FormikTextField
                  name="optOutMessage"
                  value={values.optOutMessage}
                  label="Automatic Opt Out Message"
                  bottomLabel="You can customize the automatic opt out message or use the default provided here."
                  multiline
                  fullWidth
                />
              )}
            </Box>

            <AsyncWrapper
              data={subaccountBudgetData}
              loading={subaccountBudgetLoading}
              errors={[subaccountBudgetError]}
            >
              {data =>
                data.getSubaccount.currentBudget && (
                  <Box>
                    <Typography variant="display0Bold">Budget</Typography>
                    <Typography variant="h2" sx={{ pb: "16px" }}>
                      This subaccount has a budget enforced.{" "}
                      {userIsOrgOwnerOrHigher ? (
                        <>
                          You can update the budget for this subaccount from
                          your organization's{" "}
                          <InternalLink
                            to="/organization/budgets"
                            target="_blank"
                          >
                            budgets page
                          </InternalLink>
                          .
                        </>
                      ) : (
                        <>
                          Please{" "}
                          <ExternalLink
                            href={`mailto:${data.getSubaccount.organizationOwners.map(oo => oo.email).join(",")}`}
                          >
                            contact your organization owner
                          </ExternalLink>{" "}
                          for more information about budgeting.
                        </>
                      )}
                    </Typography>
                    <Box
                      sx={{
                        width: "500px",
                        px: "24px",
                        py: "16px",
                        backgroundColor: palette.lightestGray,
                      }}
                    >
                      <BudgetDetails
                        subaccountSlug={subaccount.slug}
                        startDate={data.getSubaccount.currentBudget.startDate}
                        allocationInMillicents={
                          data.getSubaccount.currentBudget
                            .allocationInMillicents
                        }
                        usageInMillicents={
                          data.getSubaccount.currentBudget.usageInMillicents
                        }
                      />
                    </Box>
                  </Box>
                )
              }
            </AsyncWrapper>

            <Box>
              <Typography variant="display0Bold">Link Settings</Typography>
              <Typography variant="h2" sx={{ mb: 2 }}>
                Configure your settings for link shortening, domain names, and
                ActBlue link tracking.
              </Typography>
              {subaccount.linkShortenerDomains.length === 0 && (
                <Typography variant="h2" sx={{ mb: 2 }}>
                  <strong>
                    This subaccount does not currently have any domain names
                    available for link shortening.
                  </strong>{" "}
                  If you are an Organization Owner, you can add a new custom
                  domain on your organization&apos;s{" "}
                  <InternalLink to="/organization/shortlinks">
                    Shortlinks Page
                  </InternalLink>
                  .
                </Typography>
              )}

              <Typography variant="h2" sx={{ mb: 2 }}>
                Turning on link shortening will allow you to track clicks and
                conversions from links sent through your campaigns. If you turn
                this on, when you include a link in a message, we will
                automatically convert it into a custom trackable link.
              </Typography>

              {subaccount.linkShortenerDomains.length > 0 && (
                <>
                  <Typography variant="h2" sx={{ mb: 2 }}>
                    If you have multiple domain names available for your
                    organization, you can select the default one below. This
                    setting will apply by default, but can be overriden on a
                    per-script-variant basis. If you are an Organization Owner,
                    you can add a new domain name on your organization&apos;s{" "}
                    <InternalLink to="/organization/shortlinks">
                      Shortlinks Page
                    </InternalLink>
                    .
                  </Typography>
                  <Box>
                    <FormikSwitchField
                      name="linkShortenerEnabled"
                      label={`Automatically shorten links${
                        subaccount.linkShortenerDomains.length === 1
                          ? ` with ${values.linkShortenerDomainName}`
                          : ""
                      }`}
                    />
                  </Box>
                  {values.linkShortenerEnabled &&
                    subaccount.linkShortenerDomains.length > 1 && (
                      <FormikSelect
                        fullWidth
                        name="linkShortenerDomainName"
                        label="Custom Link Domain"
                      >
                        {subaccount.linkShortenerDomains.map(({ domain }) => (
                          <MenuItem key={domain} value={domain}>
                            Shorten links with {domain}
                          </MenuItem>
                        ))}
                      </FormikSelect>
                    )}
                </>
              )}
              <Typography variant="h2" sx={{ my: 2 }}>
                Adding a{" "}
                <Typography display="inline" fontFamily="monospace">
                  ?refcode2w=XXXXXX
                </Typography>{" "}
                parameter to Actblue links with a tracking parameter allows you
                to associate donations back to contacts. This is required for
                the ActBlue Webhook integration to perform donation and ROI
                tracking. If you don't enable this option, or you link to
                ActBlue via a non-Scale to Win link shortener or intermediate
                survey page, you can add this refcode manually with the{" "}
                <Typography display="inline" fontFamily="monospace">
                  add_refcode2w
                </Typography>{" "}
                Liquid template filter.
              </Typography>
              <FormikSwitchField
                name="autoAddRefcodesToABLinks"
                label="Automatically add refcode2w to ActBlue links"
              />
            </Box>

            <Box>
              <Typography variant="display0Bold">Texter Send</Typography>
              <Typography variant="h2" sx={{ mb: 2 }}>
                Allow campaign creators to choose Texter Send mode to send
                Initial Messages for their campaigns. When Texter Send mode is
                selected for a campaign, Texters will click to send Initial
                Messages in batches (batch size is customizable for each
                campaign). Disabling Texter Send for the subaccount will not
                affect existing campaigns that are currently on Texter Send
                mode.
              </Typography>
              <FormikSwitchField
                name="texterSendEnabled"
                label="Enable Texter Send"
              />
            </Box>

            <SettingsPreviewContacts />

            <FlexBox sx={{ justifyContent: "flex-end" }}>
              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText="Save Changes"
                loadingText="Saving..."
                data-testid="save-subaccount-basics-button"
                onClick={submitForm}
              />
            </FlexBox>
          </FlexBox>
        </Form>
      )}
    </Formik>
  );
}
